import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import logger from "@/lib/logger";
import {
  MagicWand, Sparkle, X, ChatCircleDots, PaperPlaneTilt, Trash, ArrowsClockwise,
} from "@phosphor-icons/react";

/* ============================================================ */
/* AI Enrich button — used inline on lens "add" forms            */
/* ============================================================ */
export function AIEnrichButton({ projectId, lens, partial, disabled, onEnriched, label = "AI Enrich" }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    const titleish = (partial?.title || partial?.statement || "").trim();
    if (!titleish) {
      toast.error("Type a short title first, then click AI Enrich.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/${lens}/ai-enrich`, partial);
      onEnriched?.(data);
      toast.success("AI enriched the entry — review and adjust before saving.");
    } catch (e) {
      const msg = e?.response?.data?.detail;
      if (e?.response?.status === 402) {
        toast.error(msg || "Monthly AI quota reached.");
      } else {
        toast.error(msg || "Enrichment failed");
      }
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={run}
      disabled={busy || disabled}
      title="Let AI fill in the rest of this entry"
      className="inline-flex items-center gap-2 h-12 px-4 border border-[#0020F5] text-[#0020F5] text-xs font-bold hover:bg-[#0020F5] hover:text-white transition-colors disabled:opacity-50"
      data-testid={`ai-enrich-${lens}-button`}
    >
      <Sparkle size={14} weight="fill" />
      {busy ? "Thinking…" : label}
    </button>
  );
}

/* ============================================================ */
/* Bulk Generate modal — prompt → preview → import               */
/* ============================================================ */
export function AIBulkGenerateModal({ projectId, lens, open, onClose, onImported }) {
  const [prompt, setPrompt] = useState("");
  const [count, setCount] = useState(5);
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState({});

  useEffect(() => {
    if (!open) { setPrompt(""); setCount(5); setItems(null); setSelected({}); setBusy(false); }
  }, [open]);

  if (!open) return null;

  const generate = async () => {
    if (!prompt.trim()) { toast.error("Describe what you want generated"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/${lens}/ai-generate`, { prompt, count });
      setItems(data.items || []);
      const sel = {};
      (data.items || []).forEach((_, i) => { sel[i] = true; });
      setSelected(sel);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Generation failed";
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const importPicked = async () => {
    if (!items) return;
    const picked = items.filter((_, i) => selected[i]);
    if (picked.length === 0) { toast.error("Pick at least one item"); return; }
    const payload = {
      source: "ai",
      filename: `ai-${lens}-${new Date().toISOString().slice(0, 10)}`,
      items: picked.map((it) => ({
        type: lens,
        title: lens === "assumption" ? (it.statement || "") : (it.title || ""),
        description: it.description || it.suggested_mitigation || "",
      })),
    };
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/integrations/import`, payload);
      toast.success(`Imported ${data.counts?.[lens] || picked.length} ${lens}(s)`);
      onImported?.(data.counts);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid={`bulk-gen-modal-${lens}`}>
      <div className="bg-white w-full max-w-3xl max-h-[88vh] overflow-hidden flex flex-col border-2 border-zinc-950">
        <header className="flex items-center justify-between px-6 py-4 border-b border-zinc-200 bg-zinc-50">
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Bulk generate · {lens}</div>
            <h3 className="font-display font-bold text-xl mt-0.5">AI generated {lens}s</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-200" data-testid="close-bulk-gen-modal">
            <X size={18} weight="bold" />
          </button>
        </header>
        <div className="p-6 overflow-y-auto flex-1">
          {!items ? (
            <div className="space-y-4">
              <p className="text-sm text-zinc-700">
                Describe what you want — ARISSAI generates structured {lens} entries from your project context.
              </p>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder={`e.g. ${lens === "assumption" ? "Critical assumptions about vendor commitments" : lens === "risk" ? "Top risks during cutover to production" : "Common issues during user acceptance testing"}`}
                rows={4}
                className="w-full px-4 py-3 border border-zinc-300 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950"
                data-testid="bulk-gen-prompt"
              />
              <div className="flex items-center gap-3">
                <label className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-500">How many</label>
                <input type="number" min="1" max="10" value={count}
                  onChange={(e) => setCount(Math.max(1, Math.min(10, Number(e.target.value))))}
                  className="w-20 h-10 px-2 border border-zinc-300 text-sm text-center"
                  data-testid="bulk-gen-count" />
                <button
                  onClick={generate}
                  disabled={busy}
                  className="ml-auto inline-flex items-center gap-2 h-11 px-5 bg-[#0020F5] text-white text-sm font-bold hover:bg-blue-700 disabled:opacity-50"
                  data-testid="bulk-gen-run"
                >
                  <MagicWand size={14} weight="fill" />
                  {busy ? "Generating…" : "Generate"}
                </button>
              </div>
              <div className="text-xs text-zinc-500">This consumes 2 AI calls from your monthly quota.</div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="text-xs text-zinc-600 mb-2">{items.length} suggestion(s). Uncheck any you don't want.</div>
              {items.length === 0 && <div className="text-sm text-zinc-500">No items returned. Try a different prompt.</div>}
              {items.map((it, idx) => {
                const title = lens === "assumption" ? it.statement : it.title;
                const desc = it.description || it.suggested_mitigation || "";
                const itemKey = `${title || "untitled"}-${idx}`;
                return (
                  <label key={itemKey} className="flex items-start gap-3 p-3 border border-zinc-200 hover:bg-zinc-50 cursor-pointer" data-testid={`bulk-gen-item-${idx}`}>
                    <input
                      type="checkbox"
                      checked={!!selected[idx]}
                      onChange={(e) => setSelected({ ...selected, [idx]: e.target.checked })}
                      className="mt-1"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-bold text-sm">{title}</div>
                      {desc && <p className="text-xs text-zinc-600 mt-1">{desc}</p>}
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>
        <footer className="px-6 py-4 border-t border-zinc-200 bg-zinc-50 flex items-center justify-between gap-3">
          <button onClick={onClose} className="h-10 px-4 border border-zinc-300 text-zinc-700 hover:border-zinc-950 text-sm" data-testid="bulk-gen-cancel">
            Cancel
          </button>
          {items && (
            <div className="flex items-center gap-2">
              <button onClick={() => { setItems(null); }} className="inline-flex items-center gap-2 h-10 px-4 border border-zinc-300 text-zinc-700 hover:border-zinc-950 text-sm" data-testid="bulk-gen-back">
                <ArrowsClockwise size={14} weight="bold" /> New prompt
              </button>
              <button onClick={importPicked} disabled={busy} className="inline-flex items-center gap-2 h-10 px-5 bg-zinc-950 text-white text-sm font-bold hover:bg-zinc-800 disabled:opacity-50" data-testid="bulk-gen-import">
                <Sparkle size={14} weight="fill" />
                {busy ? "Importing…" : `Import selected`}
              </button>
            </div>
          )}
        </footer>
      </div>
    </div>
  );
}

/* ============================================================ */
/* Floating AI Assistant per-lens                                */
/* ============================================================ */
export function AIAssistant({ projectId, lens, label }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const scrollRef = useRef();

  useEffect(() => {
    if (!open || historyLoaded) return;
    (async () => {
      try {
        const { data } = await api.get(`/projects/${projectId}/assistant/${lens}/messages`);
        setMessages(data.messages || []);
        setHistoryLoaded(true);
      } catch (err) {
        // Non-fatal — assistant history simply won't load. Surface in console
        // so we can debug if users report empty panels.
        logger.warn("Assistant history load failed:", err?.response?.data?.detail || err?.message);
      }
    })();
  }, [open, projectId, lens, historyLoaded]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async (e) => {
    e?.preventDefault();
    const msg = input.trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((p) => [...p, { role: "user", content: msg, created_at: new Date().toISOString(), id: `tmp-${Date.now()}` }]);
    setLoading(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/assistant/${lens}/messages`, { message: msg });
      setMessages((p) => [...p.slice(0, -1), data.user, data.assistant]);
    } catch (e2) {
      const detail = e2?.response?.data?.detail || "Failed to get a reply";
      toast.error(detail);
      setMessages((p) => p.slice(0, -1));
    } finally { setLoading(false); }
  };

  const clear = async () => {
    try {
      await api.delete(`/projects/${projectId}/assistant/${lens}/messages`);
      setMessages([]);
      toast.success("Chat cleared");
    } catch { toast.error("Could not clear chat"); }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 h-12 px-5 bg-zinc-950 text-white text-xs font-bold tracking-[0.1em] uppercase hover:bg-[#0020F5] transition-colors shadow-lg shadow-zinc-900/30"
        data-testid={`open-ai-assistant-${lens}`}
        aria-label="Open AI Assistant"
      >
        <ChatCircleDots size={16} weight="fill" />
        Ask {label || lens} AI
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-stretch justify-end" onClick={() => setOpen(false)}>
          <aside
            className="bg-white w-full sm:max-w-md h-full flex flex-col border-l-2 border-zinc-950"
            onClick={(e) => e.stopPropagation()}
            data-testid={`ai-assistant-panel-${lens}`}
          >
            <header className="flex items-center justify-between px-5 py-4 border-b border-zinc-200 bg-zinc-50">
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">AI Assistant</div>
                <h3 className="font-display font-bold text-lg mt-0.5">{label || lens}</h3>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={clear} className="p-2 hover:bg-zinc-200 text-zinc-600" title="Clear chat" data-testid={`clear-ai-assistant-${lens}`}>
                  <Trash size={14} weight="bold" />
                </button>
                <button onClick={() => setOpen(false)} className="p-2 hover:bg-zinc-200" data-testid={`close-ai-assistant-${lens}`}>
                  <X size={18} weight="bold" />
                </button>
              </div>
            </header>
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4 bg-white">
              {messages.length === 0 && (
                <div className="text-sm text-zinc-500 leading-relaxed">
                  <p className="mb-2">Ask me about your <strong>{label || lens}</strong> data — I see what's in this project and reply in plain English.</p>
                  <p className="text-xs text-zinc-400">Try: "What are my top 3 issues?" or "Which risks need mitigation this week?"</p>
                </div>
              )}
              {messages.map((m) => (
                <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`ai-msg-${m.role}`}>
                  <div className={`max-w-[88%] px-4 py-3 text-sm whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "bg-zinc-950 text-white" : "bg-zinc-100 text-zinc-900 border border-zinc-200"}`}>
                    {m.content}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="px-4 py-3 text-sm bg-zinc-100 text-zinc-500 border border-zinc-200">Thinking…</div>
                </div>
              )}
            </div>
            <form onSubmit={send} className="border-t border-zinc-200 p-4 bg-zinc-50">
              <div className="flex items-stretch gap-2">
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask about this lens…"
                  className="flex-1 h-11 px-3 border border-zinc-300 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950"
                  data-testid={`ai-assistant-input-${lens}`}
                />
                <button type="submit" disabled={loading || !input.trim()} className="inline-flex items-center justify-center h-11 w-11 bg-zinc-950 text-white hover:bg-[#0020F5] disabled:opacity-40" data-testid={`ai-assistant-send-${lens}`}>
                  <PaperPlaneTilt size={16} weight="fill" />
                </button>
              </div>
            </form>
          </aside>
        </div>
      )}
    </>
  );
}

/* ============================================================ */
/* Upload Review Modal — pre-import editable table               */
/* ============================================================ */
export function UploadReviewModal({ open, preview, onClose, onImport, busy }) {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (open && preview) {
      const annotated = (preview.rows || []).map((r) => ({
        ...r,
        include: r.status === "ok",
      }));
      setRows(annotated);
    }
  }, [open, preview]);

  if (!open || !preview) return null;

  const updateRow = (idx, patch) => {
    setRows((p) => p.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const handleImport = () => {
    const picked = rows
      .filter((r) => r.include && (r.title || "").trim() && ["assumption", "risk", "issue"].includes((r.type || "").toLowerCase()))
      .map((r) => ({ type: r.type, title: r.title, description: r.description || "" }));
    if (picked.length === 0) {
      toast.error("Nothing to import — fix or check at least one row.");
      return;
    }
    onImport({
      source: preview.source || "csv",
      filename: preview.filename || "",
      items: picked,
    });
  };

  const statusColor = (s) => ({
    ok: "text-[#00C7BE]",
    duplicate: "text-[#FF9500]",
    invalid: "text-[#FF3B30]",
  }[s] || "text-zinc-500");

  const includedCount = rows.filter((r) => r.include).length;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="upload-review-modal">
      <div className="bg-white w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col border-2 border-zinc-950">
        <header className="flex items-center justify-between px-6 py-4 border-b border-zinc-200 bg-zinc-50">
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Review · {preview.source?.toUpperCase()}</div>
            <h3 className="font-display font-bold text-xl mt-0.5">{preview.filename || "Uploaded file"}</h3>
            <div className="text-xs text-zinc-600 mt-1">
              {preview.counts?.ok} valid · {preview.counts?.duplicate} duplicate · {preview.counts?.invalid} invalid · {preview.counts?.total} total
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-200" data-testid="close-upload-review">
            <X size={18} weight="bold" />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 sticky top-0">
              <tr className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">
                <th className="text-left px-3 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={includedCount > 0 && includedCount === rows.length}
                    onChange={(e) => setRows((p) => p.map((r) => ({ ...r, include: e.target.checked })))}
                  />
                </th>
                <th className="text-left px-3 py-3 w-12">Row</th>
                <th className="text-left px-3 py-3 w-28">Type</th>
                <th className="text-left px-3 py-3">Title</th>
                <th className="text-left px-3 py-3">Description</th>
                <th className="text-left px-3 py-3 w-28">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={`row-${r.row ?? idx}`} className="border-t border-zinc-200 align-top" data-testid={`review-row-${idx}`}>
                  <td className="px-3 py-3">
                    <input
                      type="checkbox"
                      checked={!!r.include}
                      onChange={(e) => updateRow(idx, { include: e.target.checked })}
                      data-testid={`review-include-${idx}`}
                    />
                  </td>
                  <td className="px-3 py-3 text-xs text-zinc-500">{r.row}</td>
                  <td className="px-3 py-3">
                    <select
                      value={r.type || ""}
                      onChange={(e) => updateRow(idx, { type: e.target.value })}
                      className="h-9 px-2 border border-zinc-300 text-xs w-full"
                      data-testid={`review-type-${idx}`}
                    >
                      <option value="">—</option>
                      <option value="assumption">assumption</option>
                      <option value="risk">risk</option>
                      <option value="issue">issue</option>
                    </select>
                  </td>
                  <td className="px-3 py-3">
                    <input
                      value={r.title || ""}
                      onChange={(e) => updateRow(idx, { title: e.target.value })}
                      className="w-full h-9 px-2 border border-zinc-300 text-sm"
                      data-testid={`review-title-${idx}`}
                    />
                  </td>
                  <td className="px-3 py-3">
                    <input
                      value={r.description || ""}
                      onChange={(e) => updateRow(idx, { description: e.target.value })}
                      className="w-full h-9 px-2 border border-zinc-300 text-sm"
                      data-testid={`review-desc-${idx}`}
                    />
                  </td>
                  <td className={`px-3 py-3 text-xs font-bold uppercase tracking-[0.1em] ${statusColor(r.status)}`}>
                    {r.status}
                    {r.errors?.length > 0 && (
                      <div className="text-[10px] text-zinc-500 mt-1 normal-case font-normal">
                        {r.errors.join(", ")}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <footer className="px-6 py-4 border-t border-zinc-200 bg-zinc-50 flex items-center justify-between">
          <div className="text-xs text-zinc-600">{includedCount} of {rows.length} rows selected for import</div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="h-10 px-4 border border-zinc-300 text-zinc-700 hover:border-zinc-950 text-sm" data-testid="upload-review-cancel">
              Cancel
            </button>
            <button onClick={handleImport} disabled={busy || includedCount === 0} className="inline-flex items-center gap-2 h-10 px-5 bg-zinc-950 text-white text-sm font-bold hover:bg-zinc-800 disabled:opacity-50" data-testid="upload-review-import">
              <Sparkle size={14} weight="fill" />
              {busy ? "Importing…" : `Import ${includedCount} item${includedCount === 1 ? "" : "s"}`}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

/* ============================================================ */
/* Post-Upload progress modal — runs Solutions + Insights        */
/* ============================================================ */
export function PostUploadProgressModal({ projectId, open, onClose, onDone }) {
  const [status, setStatus] = useState("idle"); // idle | running | done | cancelled | error
  const [result, setResult] = useState(null);
  const [errMsg, setErrMsg] = useState("");
  const [elapsed, setElapsed] = useState(0);
  const cancelRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    cancelRef.current = false;
    setStatus("running");
    setResult(null);
    setErrMsg("");
    setElapsed(0);
    const tick = setInterval(() => setElapsed((s) => s + 1), 1000);
    (async () => {
      try {
        const { data } = await api.post(`/projects/${projectId}/post-upload-generate`, {}, { timeout: 180000 });
        if (cancelRef.current) return;
        setResult(data);
        setStatus("done");
        onDone?.(data);
      } catch (e) {
        if (cancelRef.current) return;
        const msg = e?.response?.data?.detail || "Auto-generation failed";
        setErrMsg(msg);
        setStatus("error");
      }
    })();
    return () => { cancelRef.current = true; clearInterval(tick); };
  }, [open, projectId, onDone]);

  if (!open) return null;

  const dismiss = () => {
    cancelRef.current = true;
    onClose?.();
  };

  // Per-step status — phase advances with elapsed time as a rough indicator.
  // Step states: pending | active | done.
  const phase = status === "done" ? "done" : status === "error" ? "error" : status;
  const stepState = (target) => {
    if (phase === "done") return "done";
    if (phase === "error") return target === "items" ? "done" : "pending";
    // running
    if (target === "items") return "done";        // import already happened before this modal
    if (target === "solutions") return elapsed < 25 ? "active" : "done";
    if (target === "insights") return elapsed < 25 ? "pending" : "active";
    return "pending";
  };

  const steps = [
    { id: "items",     label: "Items imported to lenses",                 sub: "Risks, Issues, Assumptions populated" },
    { id: "solutions", label: "Generating mitigation solutions",          sub: "Top 5 risks · top 5 issues" },
    { id: "insights",  label: "Computing project insights & health score", sub: "Executive summary + KPIs" },
  ];

  const StepIcon = ({ state }) => {
    if (state === "done")   return <span className="inline-block w-4 h-4 rounded-full bg-[#16A34A]" />;
    if (state === "active") return <span className="inline-block w-4 h-4 rounded-full border-2 border-[#0020F5] border-t-transparent animate-spin" />;
    return <span className="inline-block w-4 h-4 rounded-full border-2 border-zinc-300" />;
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="post-upload-progress-modal">
      <div className="bg-white w-full max-w-md border-2 border-zinc-950">
        <header className="px-6 py-4 border-b border-zinc-200 bg-zinc-50">
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Auto-generate</div>
          <h3 className="font-display font-bold text-lg mt-0.5">
            {status === "done" ? "All lenses populated ✓" : status === "error" ? "Auto-generation paused" : "Populating your project…"}
          </h3>
        </header>
        <div className="p-6 space-y-3">
          {/* Per-step list */}
          {status !== "done" && (
            <div className="space-y-2.5">
              {steps.map((s) => {
                const st = stepState(s.id);
                return (
                  <div key={s.id} className="flex items-start gap-3" data-testid={`post-upload-step-${s.id}`}>
                    <div className="mt-0.5"><StepIcon state={st} /></div>
                    <div className="min-w-0 flex-1">
                      <div className={`text-sm font-bold ${st === "active" ? "text-zinc-950" : st === "done" ? "text-zinc-700" : "text-zinc-400"}`}>{s.label}</div>
                      <div className="text-xs text-zinc-500">{s.sub}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {status === "running" && (
            <div className="mt-4 text-[11px] font-mono text-zinc-500">
              {String(Math.floor(elapsed / 60)).padStart(2, "0")}:{String(elapsed % 60).padStart(2, "0")} elapsed · you can dismiss — generation keeps running
            </div>
          )}
          {status === "done" && result && (
            <>
              <div className="text-sm text-zinc-900">
                <span className="font-bold">{result.solutions_added}</span> solutions generated for{" "}
                <span className="font-bold">{result.targets_processed}</span> targets.
              </div>
              <div className="mt-3 border border-zinc-200 p-3 bg-zinc-50">
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Headline</div>
                <p className="text-sm mt-1">{result.insights?.headline}</p>
                {result.insights?.health_score != null && (
                  <div className="mt-2 text-xs text-zinc-600">Health score: <span className="font-mono font-bold">{result.insights.health_score}/100</span></div>
                )}
              </div>
            </>
          )}
          {status === "error" && (
            <div className="mt-3 text-sm text-[#FF3B30] bg-[#FF3B30]/5 border border-[#FF3B30]/30 p-3">{errMsg}</div>
          )}
        </div>
        <footer className="px-6 py-4 border-t border-zinc-200 bg-zinc-50 flex justify-end gap-2">
          {status === "done" && (
            <button
              onClick={() => { dismiss(); window.location.hash = ""; }}
              className="h-10 px-5 bg-zinc-950 text-white text-sm font-bold hover:bg-[#0020F5]"
              data-testid="post-upload-view-results"
            >
              View results
            </button>
          )}
          {status !== "done" && (
            <button onClick={dismiss} className="h-10 px-5 bg-zinc-950 text-white text-sm font-bold hover:bg-zinc-800" data-testid="post-upload-dismiss">
              {status === "running" ? "Dismiss (keeps running)" : "Close"}
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}
